/**
 * OpsAlertPreferencesCard
 *
 * Admin self-service for ops alert phone number(s) and email/SMS toggles.
 * Reads/writes /api/v1/admin/settings/ops-alert-preferences. Visible only
 * to admin/super_admin (the page renders the card only when the role check
 * passes; the API also enforces with requireRole).
 *
 * @story SCRUM-1798
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

function isValidE164OrEmpty(v) {
  if (v === undefined || v === null || v === '') {return true;}
  return E164_REGEX.test(v);
}

export default function OpsAlertPreferencesCard() {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [alertPhone, setAlertPhone] = useState('');
  const [alertPhoneSecondary, setAlertPhoneSecondary] = useState('');
  const [opsAlertEmail, setOpsAlertEmail] = useState(true);
  const [opsAlertSms, setOpsAlertSms] = useState(false);
  // SCRUM-2177: Daily Health Digest opt-out (independent of ops alert channels)
  const [dailyDigestEmail, setDailyDigestEmail] = useState(true);

  const isAdmin =
    typeof hasRole === 'function' && (hasRole('admin') || hasRole('super_admin'));

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const res = await apiRequest('/api/v1/admin/settings/ops-alert-preferences');
        if (cancelled) {return;}
        if (res && res.success && res.data) {
          setAlertPhone(res.data.alert_phone || '');
          setAlertPhoneSecondary(res.data.alert_phone_secondary || '');
          setOpsAlertEmail(res.data.ops_alert_email !== false);
          setOpsAlertSms(res.data.ops_alert_sms === true);
          setDailyDigestEmail(res.data.daily_digest_email !== false);
        }
      } catch (err) {
        if (!cancelled) {setLoadError(err.message || 'Failed to load preferences');}
      } finally {
        if (!cancelled) {setLoading(false);}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // AC-08: SMS toggle on + empty phone → inline validation
  const smsWithoutPhoneError =
    opsAlertSms && !alertPhone
      ? 'An alert phone number is required to enable SMS ops alerts.'
      : null;
  const phoneFormatError =
    !isValidE164OrEmpty(alertPhone)
      ? 'Use E.164 format, e.g., +15551234567'
      : null;
  const phoneSecondaryFormatError =
    !isValidE164OrEmpty(alertPhoneSecondary)
      ? 'Use E.164 format, e.g., +15551234567'
      : null;

  const canSave =
    !saving && !smsWithoutPhoneError && !phoneFormatError && !phoneSecondaryFormatError;

  async function handleSave() {
    if (!canSave) {return;}
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await apiRequest('/api/v1/admin/settings/ops-alert-preferences', {
        method: 'PUT',
        body: JSON.stringify({
          alert_phone: alertPhone,
          alert_phone_secondary: alertPhoneSecondary,
          ops_alert_email: opsAlertEmail,
          ops_alert_sms: opsAlertSms,
          daily_digest_email: dailyDigestEmail,
        }),
      });
      if (res && res.success) {
        setSaveSuccess(true);
        // Reflect any server-side normalization (e.g., '' → null)
        if (res.data) {
          setAlertPhone(res.data.alert_phone || '');
          setAlertPhoneSecondary(res.data.alert_phone_secondary || '');
          setOpsAlertEmail(res.data.ops_alert_email !== false);
          setOpsAlertSms(res.data.ops_alert_sms === true);
          setDailyDigestEmail(res.data.daily_digest_email !== false);
        }
      } else {
        setSaveError((res && res.message) || 'Failed to save preferences');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) {return null;} // AC-11: non-admins do not see this section

  return (
    <section
      data-testid="ops-alert-preferences-card"
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600 }}>
        Ops Alert Preferences
      </h2>
      <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
        Configure how you receive operational alerts (system health, nightly
        checks, payment issues). Critical alerts can wake you up via SMS.
      </p>

      {loading ? (
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading…</p>
      ) : loadError ? (
        <p style={{ color: '#b91c1c', fontSize: '14px' }} role="alert">
          {loadError}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Primary alert phone</span>
            <input
              data-testid="alert-phone-input"
              type="tel"
              placeholder="+15551234567"
              value={alertPhone}
              onChange={(e) => setAlertPhone(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: phoneFormatError ? '1px solid #b91c1c' : '1px solid #d1d5db',
                fontSize: '14px',
              }}
            />
            {phoneFormatError && (
              <span style={{ color: '#b91c1c', fontSize: '13px' }}>
                {phoneFormatError}
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Secondary alert phone (optional)</span>
            <input
              data-testid="alert-phone-secondary-input"
              type="tel"
              placeholder="+15551234567"
              value={alertPhoneSecondary}
              onChange={(e) => setAlertPhoneSecondary(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: phoneSecondaryFormatError ? '1px solid #b91c1c' : '1px solid #d1d5db',
                fontSize: '14px',
              }}
            />
            {phoneSecondaryFormatError && (
              <span style={{ color: '#b91c1c', fontSize: '13px' }}>
                {phoneSecondaryFormatError}
              </span>
            )}
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              data-testid="ops-alert-email-toggle"
              type="checkbox"
              checked={opsAlertEmail}
              onChange={(e) => setOpsAlertEmail(e.target.checked)}
            />
            <span style={{ fontSize: '14px' }}>Receive ops alerts via email</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              data-testid="ops-alert-sms-toggle"
              type="checkbox"
              checked={opsAlertSms}
              onChange={(e) => setOpsAlertSms(e.target.checked)}
            />
            <span style={{ fontSize: '14px' }}>Receive ops alerts via SMS</span>
          </label>

          {/* SCRUM-2177: Daily Health Digest opt-out (independent toggle) */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              data-testid="daily-digest-email-toggle"
              type="checkbox"
              checked={dailyDigestEmail}
              onChange={(e) => setDailyDigestEmail(e.target.checked)}
            />
            <span style={{ fontSize: '14px' }}>
              Receive the Daily Health Digest email (6:00 AM EST)
            </span>
          </label>

          {smsWithoutPhoneError && (
            <p data-testid="sms-without-phone-error" style={{ color: '#b91c1c', fontSize: '13px', margin: 0 }}>
              {smsWithoutPhoneError}
            </p>
          )}

          {saveError && (
            <p data-testid="save-error" style={{ color: '#b91c1c', fontSize: '14px', margin: 0 }} role="alert">
              {saveError}
            </p>
          )}

          {saveSuccess && (
            <p data-testid="save-success" style={{ color: '#047857', fontSize: '14px', margin: 0 }} role="status">
              Preferences saved.
            </p>
          )}

          <div>
            <button
              data-testid="save-ops-alert-prefs"
              onClick={handleSave}
              disabled={!canSave}
              type="button"
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: canSave ? '#111827' : '#9ca3af',
                color: '#fff',
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {saving ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
